import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, makeStyles, Typography } from '@material-ui/core';
import InlineTextPopover from 'components/@common/InlineTextPopover';
import { ProductCurriculum } from 'types/schema-generated-types';
import { isPrmSetACurriculum } from '../../curriculum-util';
import { percentOf } from '../../SplitProgressBarChart/util';

type Props = {
  productCurriculum: ProductCurriculum;
  completed: number;
  assigned: number;
  total: number;
};

const LessonProgressBar: React.FC<Props> = ({ productCurriculum, completed, total, assigned }) => {
  const classes = useStyles({ completed, total, assigned });
  const { t } = useTranslation('ReportsHelpCommon');

  const title = `${productCurriculum?.name} Lesson Progress`;

  return (
    <Box className={classes.base}>
      <Box display="flex" justifyContent="space-between">
        {isPrmSetACurriculum(productCurriculum) ? (
          <InlineTextPopover
            PopoverContentProps={{ description: t('prmSetAReporting') }}
            className={classes.text}
          >
            {title}
          </InlineTextPopover>
        ) : (
          <Typography className={classes.text}>{title}</Typography>
        )}
        <Typography className={classes.text}>
          <span style={{ fontWeight: 450 }}>{completed}</span> of {total}
        </Typography>
      </Box>
      <div className={classes.bar} title={`Total: ${total}`}>
        <div className={classes.assigned} title={`Assigned: ${assigned}`} />
        <div className={classes.completed} title={`Completed: ${completed}`} />
      </div>
    </Box>
  );
};

const buildPercent = (value: number) => `${value}%`;
const colors = {
  assigned: 'rgba(51, 138, 198, 0.4)',
  completed: '#338AC6',
  total: '#F4F4F4'
};
const useStyles = makeStyles(() => ({
  base: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column'
  },
  bar: {
    display: 'flex',
    height: 12,
    borderRadius: 8,
    width: '100%',
    backgroundColor: colors.total
  },
  text: {
    fontSize: 18,
    fontWeight: 400
  },
  assigned: ({ assigned, total }: Omit<Props, 'productCurriculum'>) => ({
    backgroundColor: colors.assigned,
    width: buildPercent(percentOf(assigned, total)),
    borderRadius: 8
  }),
  completed: ({ completed, total, assigned }: Omit<Props, 'productCurriculum'>) => ({
    backgroundColor: colors.completed,
    width: buildPercent(percentOf(completed, total)),

    borderRadius: 8,
    marginLeft: `-${buildPercent(percentOf(assigned, total))}`
  }),
  total: {
    backgroundColor: colors.total,
    width: '100%',
    borderBottomRightRadius: 8,
    borderTopRightRadius: 8
  }
}));

export default LessonProgressBar;
